<template>
	<v-row dense>
		<v-col cols="12" lg="7" class="fs-stack">
			<SectionCard title="Riepilogo vendite" icon="mdi-cash-register" subtitle="importi al netto dell'IVA" data-tour="summary">
				<FinanceStatement :rows="rows" :compare="!!previous" :current-label="currentLabel" :previous-label="previousLabel" />
				<div class="fs-note mt-3">
					Le vendite contano alla data dell'ordine, i resi alla data del reso; gli ordini annullati sono esclusi. Gift card vendute e saldo usato non sono vendite: sono un debito verso il cliente che
					si chiude quando lo spende.
				</div>
			</SectionCard>

			<SectionCard title="Dall'incassato al guadagno" icon="mdi-cash-check" subtitle="quanto resta davvero" class="mt-2" data-tour="earnings">
				<FinanceStatement :rows="earnings" :compare="!!previous" :current-label="currentLabel" :previous-label="previousLabel" />
				<v-alert v-if="(report.summary || {}).shipping > 0" dense text type="warning" class="mt-3 mb-0 fs-note">
					Il costo dei corrieri non è ancora conteggiato: i {{ formatEurCents(report.summary.shipping) }} di spedizioni pagate dai clienti sono nei ricavi, quello che paghi ai corrieri no. Finché non
					arrivano le fatture dei corrieri, inseriscilo in Costi manuali (categoria Trasporti).
				</v-alert>
				<div v-if="pnl.cogsCoverage < 100" class="fs-note mt-2">
					Il costo della merce copre il {{ formatPct(pnl.cogsCoverage) }} delle vendite: i prodotti senza prezzo d'acquisto non hanno costo e il guadagno di quelli risulta più alto del vero.
				</div>
			</SectionCard>
		</v-col>
		<v-col cols="12" lg="5">
			<SectionCard title="Incassi e commissioni" icon="mdi-percent-outline" subtitle="incassato IVA inclusa" no-padding data-tour="fees">
				<template #actions>
					<v-btn text x-small color="primary" @click="$emit('open-fees')">Commissioni</v-btn>
				</template>
				<v-simple-table dense>
					<thead>
						<tr>
							<th>Fonte</th>
							<th class="text-right">Ordini</th>
							<th class="text-right">Incassato</th>
							<th class="text-right">Commissioni</th>
							<th class="text-right">Netto</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="f in fees" :key="f.id">
							<td>
								<div>{{ sourceName(f.key) }}</div>
								<div class="fs-note">
									{{ f.scope === 'channel' ? 'Marketplace' : 'Pagamento sul sito' }} ·
									<span :class="`${ORIGIN[f.origin].color.split(' ')[0]}--text`">{{ ORIGIN[f.origin].short }}</span>
								</div>
							</td>
							<td class="text-right">{{ formatNumber(f.orders) }}</td>
							<td class="text-right">{{ formatEurCents(f.collected) }}</td>
							<td class="text-right text-no-wrap">
								<span v-if="f.origin === 'none'" class="grey--text">da indicare</span>
								<span v-else-if="f.origin === 'included'" class="grey--text">in {{ sourceName(f.includedIn) }}</span>
								<template v-else>
									{{ formatEurCents(f.amount) }}
									<span v-if="f.collected > 0 && f.amount" class="fs-note">· {{ formatPct(Math.round((f.amount / f.collected) * 1000) / 10) }}</span>
								</template>
							</td>
							<td class="text-right font-weight-medium">{{ formatEurCents(f.collected - f.amount) }}</td>
						</tr>
						<tr v-if="fees.length" class="fs-total-row">
							<td>Totale</td>
							<td class="text-right">{{ formatNumber(totals.orders) }}</td>
							<td class="text-right">{{ formatEurCents(totals.collected) }}</td>
							<td class="text-right">{{ formatEurCents(totals.amount) }}</td>
							<td class="text-right">{{ formatEurCents(totals.collected - totals.amount) }}</td>
						</tr>
						<tr v-else>
							<td colspan="5" class="text-center grey--text py-4">Nessun ordine nel periodo</td>
						</tr>
					</tbody>
				</v-simple-table>
				<div v-if="missing" class="fs-note pa-3">{{ missing }} {{ missing === 1 ? 'fonte' : 'fonti' }} senza commissione indicata: inserisci il totale del mese o la tariffa in Commissioni.</div>
			</SectionCard>
		</v-col>
	</v-row>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import FinanceStatement from './FinanceStatement.vue';
import { formatEurCents, formatNumber, formatPct, sourceName, FEE_ORIGIN } from './format';

export default {
	name: 'FinanceSummary',
	components: { SectionCard, FinanceStatement },
	props: {
		report: { type: Object, required: true },
		previous: { type: Object, default: null },
		currentLabel: { type: String, default: '' },
		previousLabel: { type: String, default: '' },
	},
	computed: {
		// una riga per chi trattiene una commissione: i marketplace come canale, gli ordini del sito per metodo di pagamento
		fees() {
			// stesse fonti con chiavi diverse negli ordini (es. banktransfer e Bonifico): una riga sola
			const rows = new Map();
			for (const f of (this.report.pnl && this.report.pnl.fees) || []) {
				if (!(f.orders > 0 || f.amount)) continue;
				const id = `${f.scope}:${String(sourceName(f.key)).toLowerCase()}`;
				const prev = rows.get(id);
				if (!prev) rows.set(id, { ...f, id });
				else
					rows.set(id, {
						...prev,
						orders: prev.orders + f.orders,
						collected: prev.collected + f.collected,
						amount: prev.amount + f.amount,
						origin: prev.origin === f.origin ? prev.origin : prev.origin === 'none' ? f.origin : prev.origin,
					});
			}
			return [...rows.values()];
		},
		totals() {
			return this.fees.reduce((t, f) => ({ orders: t.orders + (f.orders || 0), collected: t.collected + (f.collected || 0), amount: t.amount + (f.amount || 0) }), {
				orders: 0,
				collected: 0,
				amount: 0,
			});
		},
		missing() {
			return this.fees.filter(f => f.origin === 'none').length;
		},
		pnl() {
			return this.report.pnl || {};
		},
		// dal totale pagato dai clienti a quello che resta: stessi numeri del conto economico
		earnings() {
			const pick = r => {
				const s = (r && r.summary) || {};
				const c = (r && r.pnl) || {};
				const fees = ((c.marketplaceFees && c.marketplaceFees.total) || 0) + ((c.paymentFees && c.paymentFees.total) || 0);
				return {
					total: s.totalSales,
					taxes: s.taxes,
					fees,
					cash: (s.totalSales || 0) - (s.taxes || 0) - fees,
					cogs: c.cogs,
					commissions: c.commissions && c.commissions.total,
					ads: c.ads && c.ads.total,
					expenses: c.expenses && c.expenses.total,
					result: c.operatingResult,
				};
			};
			const cur = pick(this.report);
			const prev = this.previous ? pick(this.previous) : {};
			const row = (label, key, extra = {}) => ({ label, value: cur[key], previous: prev[key], ...extra });
			return [
				row('Totale pagato dai clienti', 'total'),
				row('IVA da versare', 'taxes', { negative: true, indent: true }),
				row('Commissioni (marketplace e pagamenti)', 'fees', { negative: true, indent: true }),
				row('Netto incassato', 'cash', { total: true, hint: 'Quello che arriva sul conto, già senza IVA e commissioni.' }),
				row('Costo della merce venduta', 'cogs', { negative: true, indent: true }),
				row('Provvigioni agenti e partner', 'commissions', { negative: true, indent: true }),
				...(cur.ads || prev.ads ? [row('Pubblicità (dalle piattaforme)', 'ads', { negative: true, indent: true })] : []),
				row('Spese (fatture passive e costi manuali)', 'expenses', { negative: true, indent: true }),
				row('Guadagno', 'result', { total: true, hint: 'È il risultato operativo del conto economico.' }),
			];
		},
		rows() {
			const s = this.report.summary || {};
			const p = (this.previous && this.previous.summary) || {};
			const row = (label, key, extra = {}) => ({ label, value: s[key], previous: p[key], ...extra });
			return [
				row('Vendite lorde dei prodotti', 'grossSales', { hint: 'Prezzo dei prodotti venduti, IVA esclusa, prima di sconti e resi. Spedizioni e supplementi sono sotto, a parte.' }),
				row('Sconti', 'discounts', {
					negative: true,
					indent: true,
					hint: "Sconti dei bundle e sconti d'ordine Shopify. I coupon del sito sono già dentro i prezzi delle righe: il lordo prima del coupon non viene salvato.",
				}),
				row('Resi e rimborsi', 'returns', { negative: true, indent: true, hint: 'Rimborsi del periodo, alla data del reso (non dell’ordine).' }),
				row('Vendite nette dei prodotti', 'netSales', { total: true }),
				row('Spedizioni pagate dai clienti', 'shipping', { hint: 'Quanto i clienti hanno pagato di spedizione, IVA esclusa. È un ricavo: il costo dei corrieri è un’altra cosa e va tra i costi.' }),
				row('Supplementi pagati dai clienti', 'surcharges', { hint: 'Il costo in più che il cliente paga per certi metodi di pagamento, tipicamente il contrassegno. IVA esclusa.' }),
				row('IVA', 'taxes', { hint: 'IVA compresa nei prezzi pagati dai clienti: non è tua, va versata.' }),
				row('Totale pagato dai clienti', 'totalSales', { total: true, hint: 'Tutto quello che i clienti hanno pagato, IVA inclusa.' }),
				row('Gift card vendute', 'giftCardsSold', { indent: true }),
				row('Pagato con saldo / gift card', 'walletUsed', { indent: true }),
			];
		},
	},
	data() {
		return { ORIGIN: FEE_ORIGIN };
	},
	methods: { formatEurCents, formatNumber, formatPct, sourceName },
};
</script>

<style scoped>
/* card una sotto l'altra nella colonna: l'altezza al 100% di SectionCard le farebbe uscire dalla colonna */
.fs-stack ::v-deep .section-card {
	height: auto;
}
.fs-total-row td {
	font-weight: 600;
	background: #f7f9fc;
}
.fs-note {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
